import { useAuth0 } from '../react-auth0-spa';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ErrorMessage from '../components/errorMessage';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  createDocumentCheck,
  fetchSdkToken,
  pollDocumentCheck,
  refreshSdkToken,
} from '../utils/api';
// eslint-disable-next-line no-unused-vars
import { Onfido } from 'onfido-sdk-ui';
import { isPasswordStrongEnough } from '../utils/password';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import './PassportVerificationContainer.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Row } from 'react-bootstrap';
import { currentBrand } from '../utils/brand';
import { getTranslation } from '../utils/translations';
import { onfidoBrandThemes } from '../utils/onfidoBrandThemes'

const requiredFieldMsg = getTranslation('requiredField');
const schema = yup.object({
  password1: yup.string().required(requiredFieldMsg),
  password2: yup.string().required(requiredFieldMsg),
});

const PassportVerificationStepEnum = {
  Input: 'Input',
  Verification: 'Verification',
  Retry: 'Retry',
  Loading: 'Loading',
  Verified: 'Verified',
  Registered: 'Registered',
  Errored: 'Errored',
  Polling: 'Polling',
};

export const PassportVerificationContainer = (props) => {
  const { loading, user } = useAuth0();
  const [metaDataLoaded, setMetaDataLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [sdkToken, setSdkToken] = useState(null);
  const [applicantId, setApplicantId] = useState(null);
  const [onfidoOut] = useState(null);
  const [uiStep, _setUiStep] = useState(PassportVerificationStepEnum.Loading);

  const setPassword = getTranslation('setPassword');
  const setPasswordText = getTranslation('setPasswordText');
  const password = getTranslation('password');
  const repeatPassword = getTranslation('repeatPassword');
  const passwordValidate1 = getTranslation('passwordValidate1');
  const passwordValidate2 = getTranslation('passwordValidate2');
  const needHelp = getTranslation('needHelp');
  const descriptionPassword = getTranslation('descriptionPassword');
  const timeOutErrorMessage = getTranslation('timeOutErrorMessage');
  const countryMessageForCheckError = getTranslation('countryMessageForCheckError');
  const brandName = process.env.REACT_APP_BRAND;

  useEffect(() => {
    const addMetaData = async () => {
      if (!loading && user) {
        try {
          const {
            data: { user_metadata: metaData },
          } = await axios.get(`/api/auth0/addMetaData/${user.sub}`);
          setMetaDataLoaded(true);
          defineUIStepBasedOnMetaData(metaData);
        } catch (e) {
          // Not setting setMetaDataLoaded(true)
          // Ensures that input form and Photo validation aren't rendered
          // If we error out in getting user metadata
          if (error.response) {
            console.log(error.response);
          }
          const errorMsg = error.response
            ? `Error ${error.response.status} accessing ${error.response.config.url}`
            : 'Error in getting user data. Are we here?';
          setError(true);
          setErrorMessage(errorMsg);
        }
      }
    };
    // noinspection JSIgnoredPromiseFromCall
    addMetaData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  const setUiStep = (newStep) => {
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'onfido_verification_step',
        'step': newStep
      });
    }

    _setUiStep(newStep);
  };

  const passwordInputs = () => {
    const brandName = process.env.REACT_APP_BRAND;
    return (
      <div className="polling password-step">
        <h1 className="password-step__title text-center">{setPassword}</h1>
        <p className="password-step__subtitle mt-2">
          {setPasswordText}
        </p>
        <Formik
          initialValues={{
            password1: '',
            password2: '',
          }}
          validationSchema={schema}
          onSubmit={async (values) => {
            const { sdkToken, applicantId, metaData } = await fetchSdkToken(
              user.email,
              user.sub,
              values.password1,
            );
            setSdkToken(sdkToken);
            setApplicantId(applicantId);
            defineUIStepBasedOnMetaData(metaData);
          }}
          validate={(values) => {
            const errors = {};
            if (values.password1 && !isPasswordStrongEnough(values.password1)) {
              errors.password1 = passwordValidate1;
            }
            if (values.password2 && values.password2 !== values.password1) {
              errors.password2 = passwordValidate2;
            }
            return errors;
          }}
        >
          {({ handleSubmit, handleChange, values, touched, errors }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Row>
                <Form.Group as={Col} controlId="validationFormik01">
                  <Form.Control
                    type="password"
                    name="password1"
                    placeholder={password}
                    value={values.password1}
                    onChange={handleChange}
                    isValid={touched.password1 && !errors.password1}
                    isInvalid={touched.password1 && !!errors.password1}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password1}
                  </Form.Control.Feedback>
                </Form.Group>
              </Form.Row>
              <Form.Row>
                <Form.Group as={Col} controlId="validationFormik01">
                  <Form.Control
                    type="password"
                    name="password2"
                    placeholder={repeatPassword}
                    value={values.password2}
                    onChange={handleChange}
                    isValid={touched.password2 && !errors.password2}
                    isInvalid={touched.password2 && !!errors.password2}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password1}
                  </Form.Control.Feedback>
                </Form.Group>
              </Form.Row>
              <Button disabled={props.disabled} type="submit">
                {setPassword}
              </Button>
              <Form.Text className="text-muted text-center my-3 py-2">
                {descriptionPassword}
              </Form.Text>
              <div className="password-step__need">
                <a
                  href={`mailto:support@${brandName}.com`}
                  className="password-step__need_a d-flex justify-content-end pt-3 text-muted"
                >
                  <img src="/shared/msg.svg" className="mr-2" alt="mail" />
                  {needHelp}
                </a>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  const passportVerification = () => {
    window.setTimeout(initializeVerificationSDK, 400);
    return <div id="onfido-mount" className="onfido-mount" />;
  };

  const initializeVerificationSDK = () => {
    if (onfidoOut && !document.querySelector('#onfido-mount')) {
      return;
    }

    const theme = onfidoBrandThemes[brandName] || onfidoBrandThemes.frontnode

    Onfido.init({
      token: sdkToken,
      containerId: 'onfido-mount',
      onComplete,
      steps: [
        {
          type: 'welcome',
        },
        {
          type: 'document',
          options: {
            documentTypes: {
              driving_licence: false,
              national_identity_card: true,
              residence_permit: false,
              passport: true,
            },
          },
        },
        {
          type: 'face',
          options: {
            requestedVariant: 'video',
          },
        },
        {
          type: 'complete',
        },
      ],
      theme,
      onError,
    });
  };

  const onComplete = async () => {
    try {
      const { checkId } = await createDocumentCheck(applicantId);
      if (!checkId) {
        console.log('unknown error');
        setUiStep(PassportVerificationStepEnum.Retry);
        return;
      }
      tearDown();
      setUiStep(PassportVerificationStepEnum.Polling);
      const isVerified = await pollDocumentCheck(applicantId);
      if (!isVerified) {
        onError(new Error(timeOutErrorMessage));
        return;
      }
      setUiStep(PassportVerificationStepEnum.Verified);
      await new Promise((resolve) => setTimeout(resolve, 4000));
      window.location.href = `${process.env.REACT_APP_DASHBOARD_LOGIN_URL}login?action=login&country=eu`;
    } catch (e) {
      console.log(`Error: ${e.message}`);
      if (e.message === 'Verification reports failed') {
        setErrorMessage(
          `There were some issues raised during your verification process. Please write our support at ${
            currentBrand().emailRegistration
          } and make sure to mention the applicant id: ${applicantId}`,
        );
        setUiStep(PassportVerificationStepEnum.Errored);
        return;
      }
      if (
        e.message &&
        e.message.startsWith(countryMessageForCheckError)
      ) {
        setErrorMessage(e.message);
      }
      setUiStep(PassportVerificationStepEnum.Retry);
      tearDown();
    }
  };

  const onError = (err) => {
    console.log('onError');
    console.log(err);
    setError(err);
    setErrorMessage(err.message);
    setUiStep(PassportVerificationStepEnum.Retry);
    tearDown();
  };

  const loader = () => (
    <div className="spinner">
      <div className="dot1" />
      <div className="dot2" />
    </div>
  );

  const isSdkTokenExpired = (metadata) => {
    if (!metadata.sdkTokenExpiry) {
      return true;
    }
    return new Date(metadata.sdkTokenExpiry).getTime() < new Date().getTime();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defineUIStepBasedOnMetaData = async (metaData) => {
    if (
      !metaData ||
      Object.keys(metaData).length === 0 ||
      !metaData.encryptedPassword ||
      !metaData.sdkToken
    ) {
      setUiStep(PassportVerificationStepEnum.Input);
      return;
    }
    if (isSdkTokenExpired(metaData)) {
      const {
        data: { user_metadata: newMetaData },
      } = await refreshSdkToken(metaData.applicantId);
      await defineUIStepBasedOnMetaData(newMetaData);
      return;
    }
    if (metaData.verificationLevel === 0) {
      setSdkToken(metaData.sdkToken);
      setApplicantId(metaData.applicantId);
      setUiStep(PassportVerificationStepEnum.Verification);
      return;
    }
    if (metaData.verificationLevel === 1) {
      setUiStep(PassportVerificationStepEnum.Registered);
      tearDown();
    }
  };

  const tearDown = () => {
    if (!onfidoOut) {
      return;
    }
    onfidoOut.tearDown();
  };

  const uiForStep = () => {
    if (uiStep === PassportVerificationStepEnum.Loading) {
      return loader();
    }
    if (uiStep === PassportVerificationStepEnum.Input) {
      return passwordInputs();
    }
    if (uiStep === PassportVerificationStepEnum.Verification) {
      return passportVerification();
    }
    if (uiStep === PassportVerificationStepEnum.Verified) {
      return successView();
    }
    if (uiStep === PassportVerificationStepEnum.Registered) {
      return registeredView();
    }
    if (uiStep === PassportVerificationStepEnum.Retry) {
      return retryView();
    }
    if (uiStep === PassportVerificationStepEnum.Polling) {
      return polling();
    }
  };

  const polling = () => {
    document.getElementById('onfido-sdk').style.display = 'none';

    return (
      <div className="polling">
        <h2>{getTranslation('verificationInProgress')}</h2>
        <p>{getTranslation('verificationTime')}</p>
        <div className="spinner">
          <div className="dot1"></div>
          <div className="dot2"></div>
        </div>
      </div>
    );
  };

  const retryView = () => {
    document.getElementById('onfido-sdk').style.display = 'none';

    return (
      <div className="polling form">
        <h2>{getTranslation('tryAgain')}</h2>
        <div className="try-again">
          <Button
            variant="light"
            className="btn cancel"
            onClick={onCancelClick}
          >
            {getTranslation('cancel')}
          </Button>
          <Button className="btn primary" onClick={onRetryClick}>
            {getTranslation('retry')}
          </Button>
        </div>
      </div>
    );
  };

  const onRetryClick = () => {
    // eslint-disable-next-line no-restricted-globals
    location.reload();
  };

  const onCancelClick = () => {
    const url = process.env.REACT_APP_LANDING_PAGE_URL;
    // eslint-disable-next-line no-restricted-globals
    location.href = url;
  };

  const successView = () => (
    <div className="polling">
      <h2>{getTranslation('successTitle')}</h2>
      <p>{getTranslation('successMessage')}</p>
      <p>
        {getTranslation('redirectLinkMessage')}{' '}
        <a href={`${process.env.REACT_APP_DASHBOARD_LOGIN_URL}login?action=login&country=eu`}>
          {getTranslation('dashboardLink')}
        </a>.
      </p>
    </div>
  );

  const registeredView = () => (
    <div>
      <h3>{getTranslation('readyToLoginTitle')}</h3>
      <p>{getTranslation('readyToLoginMessage')}</p>
      <p>
        {getTranslation('visit')}&nbsp;
        <a href={`${process.env.REACT_APP_DASHBOARD_LOGIN_URL}login?action=login&country=eu`}>
          {getTranslation('dashboardLink')}&nbsp;
        </a>
        {getTranslation('ableToLoginMessage')}
      </p>
    </div>
  );

  return (
    <div className="content">
      <ErrorMessage
        error={
          uiStep === PassportVerificationStepEnum.Retry ||
          uiStep === PassportVerificationStepEnum.Errored
        }
        errorMessage={errorMessage}
      />
      {metaDataLoaded === true ? uiForStep() : loader()}
    </div>
  );
};
